import {
  CaretLeft,
  Check,
  NumberCircleThree,
  Wallet,
  CreditCard,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSelectPaymentOption from "../../hooks/useSelectPaymentOption";
import { useTrips } from "../../context/TripsContext";
import { PaymentOption } from "../../@types";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import useBnplGate from "../../hooks/useBnplGate";
import CompleteSetupTaskList, {
  useStartSetupTask,
} from "../../components/CompleteSetupTaskList";
import BnplSetupGateDialog from "../../components/BnplSetupGateDialog";
import BookingRecoveryNotice from "../../components/BookingRecoveryNotice";
import { useAuth } from "../../context/AuthContext";
import { APIError } from "../../api";
import { formatAsMoney, parseMoney } from "../../helpers/numbers";
import { clearCheckoutResume } from "../../utils/checkoutResume";

const BNPL_OPTION_CODE = "002";
const WALLET_OPTION_CODE = "003";
const INSTALLMENT_SETUP_INCOMPLETE = "INSTALLMENT_SETUP_INCOMPLETE";

/** Setup tasks started from here come back to this screen with BNPL re-checked. */
const THIS_PATH = "/aviation/select-payment-method";
const RESUME_STATE = { resumeBnplGate: true };

const paymentOptions = [
  {
    id: "9d06b2ac-6896-4a04-ae6a-e9e34275f062",
    public_id: "64074244-80fd-44f1-bee1-74e908928ca5",
    name: "Pay with card ",
    description: "Pay with your linked card ",
    code: "001",
    icon: CreditCard,
    disabled: true,
  },
  {
    id: "9d06b2ac-6896-4a04-ae6a-e9e34275f062",
    public_id: "64074244-80fd-44f1-bee1-74e908928ca5",
    name: "Pay in Installments",
    description: "Split your payment into 3 convenient installments.",
    code: "002",
    icon: NumberCircleThree,
  },
  {
    id: "9d06b2ac-6896-4a04-ae6a-e9e34275f064",
    public_id: "64074244-80fd-44f1-bee1-74e908928caj",
    name: "Pay Now with Wallet",
    description: "Instant payment using your wallet balance.",
    code: "003",
    icon: Wallet,
  },
];

const SelectPaymentMethod = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const startSetupTask = useStartSetupTask();

  // Coming back from a setup task started at the installment gate: keep
  // installments selected and re-check the gate once the account has loaded.
  const resumingBnplGate = !!(
    location.state as { resumeBnplGate?: boolean } | null
  )?.resumeBnplGate;

  // Back from funding the wallet for this booking: keep the wallet selected.
  const preferWallet = !!(
    location.state as { preferWallet?: boolean } | null
  )?.preferWallet;

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentOption | null>(() => {
      const code = resumingBnplGate
        ? BNPL_OPTION_CODE
        : preferWallet
          ? WALLET_OPTION_CODE
          : null;
      return paymentOptions.find((o) => o.code === code) ?? null;
    });

  const {
    isBnplAllowed,
    isCheckingBnplAccess,
    firstTaskPending,
    pendingTasks,
    isGateOpen,
    openGate,
    closeGate,
    isSetupListOpen,
    openSetupList,
    closeSetupList,
  } = useBnplGate();

  const walletBalance = parseMoney(user?.wallet?.available_balance);

  // Once the customer picks a way forward, drop the return flags so going back
  // to this screen later doesn't reopen the gate or reselect for them.
  const clearReturnFlags = () => {
    if (resumingBnplGate || preferWallet) {
      navigate(location.pathname, { replace: true, state: null });
    }
  };

  const selectWallet = () => {
    clearReturnFlags();
    closeGate();
    setSelectedPaymentMethod(
      paymentOptions.find((o) => o.code === WALLET_OPTION_CODE) ?? null
    );
  };

  const {
    flightData,
    submitingNewFlightData,
    isSubmitingNewFlightDataError,
    submitNewFlightDataError,
  } = useTrips();

  const {
    selectPaymentOption,
    selectingPaymentOption,
    isSelectingPaymentOptionError,
    selectPaymentOptionError,
  } = useSelectPaymentOption({
    onSuccess(data) {
      // Checkout is moving on to payment; no setup detour left to resume.
      clearCheckoutResume();
      if (data?.data?.option_data?.tag === "pay_later") {
        navigate("/aviation/pay-later", {
          state: {
            paymentData: data?.data,
          },
        });
      } else if (data?.data?.option_data?.tag === "pay_now_card") {
        navigate("/aviation/pay-now", {
          state: {
            paymentData: data?.data,
          },
        });
      } else if (data?.data?.option_data?.tag === "pay_now_wallet") {
        navigate("/aviation/pay-now", {
          state: {
            paymentData: data?.data,
          },
        });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    },
    onError(error) {
      // The server's own installment floor refused. Show the checklist path
      // (the message itself is rendered below) when there is setup left to do.
      const code = (error instanceof APIError
        ? (error.responseData?.data as { error_code?: string } | undefined)
        : undefined)?.error_code;
      if (code === INSTALLMENT_SETUP_INCOMPLETE && pendingTasks.length > 0) {
        openGate();
      }
    },
    onMutate() {},
  });

  const loading = useMemo(
    () => selectingPaymentOption || submitingNewFlightData,
    [selectingPaymentOption, submitingNewFlightData]
  );

  // A refresh wipes the in-memory booking; the client can't prove a stored id is
  // this user's and unpaid, so restart rather than resume.
  const bookingId = flightData?.id || "";
  const cannotContinue = !bookingId && !submitingNewFlightData;

  // Re-check once per mount. If the profile refresh from the finished task is
  // still in flight, the dialog's content follows the live account, so it
  // settles on what is actually left (or hides when nothing is).
  const resumeChecked = useRef(false);
  useEffect(() => {
    if (!resumingBnplGate || !bookingId || isCheckingBnplAccess) return;
    if (resumeChecked.current) return;
    resumeChecked.current = true;
    if (!isBnplAllowed) openGate();
  }, [resumingBnplGate, bookingId, isCheckingBnplAccess, isBnplAllowed, openGate]);

  return (
    <div>
      <BnplSetupGateDialog
        isOpen={isGateOpen}
        onClose={closeGate}
        firstTaskPending={firstTaskPending}
        pendingTasks={pendingTasks}
        onStartTask={(task) => {
          closeGate();
          startSetupTask(task, {
            returnTo: THIS_PATH,
            returnState: RESUME_STATE,
            bookingId: bookingId || undefined,
          });
        }}
        onViewAllSteps={openSetupList}
        onPayWithWallet={selectWallet}
      />
      <CompleteSetupTaskList
        isOpen={isSetupListOpen}
        handleClose={closeSetupList}
        title="Finish setup to pay in installments"
        subtitle="Complete these steps to unlock installments. Your selected flight is kept while you do."
        returnTo={THIS_PATH}
        returnState={RESUME_STATE}
        bookingId={bookingId || undefined}
      />

      {loading && (
        <div className="fixed top-0 z-[100] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      <div className="flex items-center justify-center gap-10 px-5 py-20 text-center flex-col">
        <div className="flex flex-col gap-2">
          <div className="font-semibold text-3xl">Select Payment Mode</div>
          <div className="text-gray-500">
            Choose how you want to pay for your flight booking below.
          </div>
        </div>
        <div className="c-groupedSelect c-groupedSelect--vertical">
          {paymentOptions?.map((option, key) => {
            return (
              <button
                key={key}
                disabled={option.disabled}
                onClick={() => setSelectedPaymentMethod(option)}
                className={`c-groupedSelect__option ${
                  selectedPaymentMethod?.code === option.code && "active"
                } ${
                    option.disabled ? "opacity-50 cursor-not-allowed grayscale" : ""
                  }`}
              >
                <div className="text-visaro-orange rounded-full p-3 bg-visaro-orange/10">
                  <option.icon weight="bold" size={24} />
                </div>
                <div className="flex-1 flex-col text-left w-full overflow-hidden">
                  <span className="text-lg font-medium">{option.name}</span>
                  <div className="text-sm text-gray-700 mt-1">
                    {option.description}
                  </div>
                  {option.code === WALLET_OPTION_CODE && user?.wallet && (
                    <div className="text-sm text-gray-500 mt-1">
                      Balance: ₦{formatAsMoney(walletBalance.toFixed(2))}
                    </div>
                  )}
                </div>
                {selectedPaymentMethod?.code === option.code ? (
                  <div className="bg-visaro-orange p-2 rounded-full">
                    <Check className="text-white" weight="bold" size={16} />
                  </div>
                ) : (
                  <div className="p-2 rounded-full">
                    <Check
                      className="text-white invisible"
                      weight="bold"
                      size={16}
                    />
                  </div>
                )}
              </button>
            );
          })}

          {cannotContinue && <BookingRecoveryNotice />}
          {isSelectingPaymentOptionError && (
            <div className="text-red-500 py-4 text-left">
              {selectPaymentOptionError?.message}
            </div>
          )}
          {isSubmitingNewFlightDataError && (
            <div className="text-red-500 py-4 text-left">
              {submitNewFlightDataError?.message}
            </div>
          )}
          <br />
          <div className="flex justify-between gap-2 w-full">
            <button
              type="button"
              onClick={() => navigate("/home")}
              className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
            >
              <CaretLeft weight="bold" size={20} />
              <span>Back</span>
            </button>
            <button
              onClick={() => {
                if (selectedPaymentMethod && bookingId) {
                  // Only BNPL is gated on account setup. Wallet payment is open
                  // to everyone; the backend checks the balance at confirm.
                  if (
                    selectedPaymentMethod.code === BNPL_OPTION_CODE &&
                    !isBnplAllowed
                  ) {
                    if (isCheckingBnplAccess) return;
                    openGate();
                    return;
                  }

                  clearReturnFlags();
                  selectPaymentOption({
                    booking_id: bookingId,
                    payment_option_id: selectedPaymentMethod.code,
                  });
                }
              }}
              disabled={!selectedPaymentMethod || !bookingId}
              className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectPaymentMethod;
